import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * A small colored pill for an arbitrary labeled value — the generic sibling
 * of RiskBadge/Badge, promoted out of PreConsultSummaryView.jsx once a
 * second read-only stage-summary view (StageSummaryView.jsx) needed the same
 * thing (a queue Priority, a Resolve outcome, a Pharmacy dispensed flag).
 * Unlike RiskBadge (a severity verdict) or Badge (a fixed clinical/financial
 * status), a ValueChip carries whatever tone the caller assigns it — it is
 * the display half of what SegmentedControl lets a user *pick*.
 */
const TONE_CLASS: Record<string, string> = {
  success: 'border-success/40 bg-success-soft text-success-ink',
  warn: 'border-warn/40 bg-warn-soft text-warn-ink',
  caution: 'border-caution-ink/40 bg-warn-soft text-caution-ink',
  danger: 'border-danger/40 bg-danger-soft text-danger-ink',
  info: 'border-info/40 bg-info-soft text-info-ink',
  neutral: 'border-teal/40 bg-accent text-teal',
};

export type ValueChipTone = keyof typeof TONE_CLASS;

export interface ValueChipProps {
  tone?: ValueChipTone;
  children: ReactNode;
  className?: string;
}

export function ValueChip({ tone = 'neutral', children, className }: ValueChipProps) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12.5px] font-semibold', TONE_CLASS[tone] || TONE_CLASS.neutral, className)}>
      {children}
    </span>
  );
}
